import { Box, Grid, GridItem, Progress, Stack } from '@chakra-ui/react';
import type { FC } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { MdError } from 'react-icons/md';
import {
	FaceliftCircularProgress,
	FaceliftProgress,
} from '@/components/Atoms/FaceliftProgress';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { timePassed } from '@/utils/bgUtils';
import type { BgProcessModule } from '../../../hooks/service-hooks/bgProcess';
import Show from '../Show';

export type ProcessItem = {
	status: string;
	moduleName: BgProcessModule | string;
	moduleAction: string;
	subTitle?: string;
  response?: Record<string, any>;
	customData?: Record<string, any>;
	startTime?: string;
	endTime?: string;
	errorDescription?: string;
};

type IBgProcessesProps = {
	processes: ProcessItem[];
	moduleRedirection: (item: ProcessItem) => void;
	handleClose: () => void;
};

const textColors: Record<string, string> = {
	completed: '#2E7D32',
	failed: 'red.500',
	pending: 'gray.500',
};

const BgProcessListItem: FC<IBgProcessesProps> = ({
	processes,
	moduleRedirection,
	handleClose,
}) => {
	const getStatusText = (status: string) => {
		switch (status) {
			case 'queued':
				return 'Queued';
			case 'running':
				return 'In Progress';
			case 'completed':
				return 'Completed';
			default:
				return 'Failed';
		}
	};

	return (
		<Stack>
			<Box display="flex" justifyContent="space-between" mb={2} paddingLeft={2} paddingRight={2}>
				<FaceliftText fontWeight={700}>Process History</FaceliftText>
				<FaceliftText fontWeight={700}>
					{processes.filter((item) => item.status === 'completed').length}/
					{processes.length}
				</FaceliftText>
			</Box>

			{/* Scrollable container */}
			<Box maxH="280px" overflowY="auto" gap={1}>
				{processes.length > 0 ? (
					processes.map((item, index) => (
						<Grid
							key={index!}
							templateColumns="10% 70% 20%"
							p={1}
							px={4}
							gap={1}
							cursor={
								item.status === 'completed'
									? 'pointer'
									: 'not-allowed'
							}
							// paddingLeft={2} paddingRight={2}
							_hover={
								item.status === 'completed'
									? {
											bg: 'brand.solidGradient',
											color: 'white',
											cursor: 'pointer',
											gap: 1,
											transition: '0.2s ease-in-out all',
											'& p': {
												color: 'white !important',
											},
											'& .linear-progress': { display: 'none' },
										}
									: {
											cursor: 'not-allowed',
									}
							}
              onClick={() => item.status === 'completed' && moduleRedirection(item)}
						>
							{/* Icons */}
							<GridItem
								display={'flex'}
								alignItems={'center'}
								textAlign="center"
							>
								<Show when={item.status === 'completed'}>
									<FaCheckCircle color="#2E7D32" size={18} />
								</Show>
								<Show when={item.status === 'failed'}>
									<MdError color="red" size={22} />
								</Show>
								<Show
									when={
										item.status === 'pending' ||
										item.status === 'queued' ||
										item.status === 'running'
									}
								>
									<FaceliftCircularProgress value={null} size="sm" />
								</Show>
							</GridItem>

							{/* Title + description */}
							<GridItem>
								<FaceliftText fontWeight={600} fontSize="14px">
									{item.subTitle}
								</FaceliftText>

								<Show when={item.status === 'completed'}>
									<FaceliftText fontSize="xs" color="gray.500">
										Your data sync is complete. The information is now available
										on your portal
									</FaceliftText>
								</Show>

								<Show
									when={
										item.status === 'pending' ||
										item.status === 'running' ||
										item.status === 'queued'
									}
								>
									<FaceliftText fontSize="xs" color="gray.500">
										We're syncing your data. It will be available soon.
									</FaceliftText>
								</Show>

								<Show when={item.status === 'failed'}>
									<FaceliftText fontSize="xs" color="gray.500">
										Your data sync has failed. Please contact administrator.
									</FaceliftText>
								</Show>
							</GridItem>

							{/* Status */}
							<GridItem textAlign="right">
								<FaceliftText
									color={textColors[item.status]}
									fontWeight={600}
									fontSize="12px"
								>
									{getStatusText(item.status)}
								</FaceliftText>
								<FaceliftText fontSize="10px" color="gray.500">
									{timePassed(item.startTime!)}
								</FaceliftText>
							</GridItem>

							<Show
								when={
									item.status === 'queued' ||
									item.status === 'running' ||
									item.status === 'pending'
								}
							>
								<GridItem colStart={2} colSpan={2} pt={1} my={2}>
									<FaceliftProgress value={null} size="sm" />
								</GridItem>
							</Show>

							<GridItem colSpan={3}>
								<Box height="1px" bg="gray.200" my={1} className='linear-progress' />
							</GridItem>
						</Grid>
					))
				) : (
					<Box textAlign="center" py={2}>
						<FaceliftText>No Active Processes</FaceliftText>
					</Box>
				)}
			</Box>
		</Stack>
	);
};

export default BgProcessListItem;
